import { apiRequest } from './client';
import type { DeviceRepository } from '../../application/ports/DeviceRepository';
import type { Device } from '../../domain/Device';
import type { StationNow } from '../../domain/Lot';
import type { ComponentKind, DeviceComponent } from '../../domain/Component';
import type { ValveSummary } from '../../domain/Irrigation';
import type { StationThresholds } from '../../domain/Thresholds';
import type { ThresholdsResponse } from '../../application/ports/DeviceRepository';

export class HttpDeviceRepository implements DeviceRepository {
  list() {
    return apiRequest<{ devices: Device[] }>('/devices');
  }

  // tz: para cortar "la lluvia de hoy" en el día del usuario.
  listNow() {
    const tz = encodeURIComponent(
      Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    );
    return apiRequest<{ now: Record<string, StationNow> }>(
      `/devices/now?tz=${tz}`,
    );
  }

  get(id: string) {
    return apiRequest<{ device: Device }>(`/devices/${id}`);
  }

  create(input: {
    name: string;
    components?: { kind: ComponentKind; channel?: number; name?: string | null }[];
    mqttTopic?: string;
    siteId?: string | null;
    lat?: number | null;
    lng?: number | null;
  }) {
    return apiRequest<{ device: Device }>('/devices', { method: 'POST', body: JSON.stringify(input) });
  }

  update(
    id: string,
    input: Partial<{
      name: string;
      siteId: string | null;
      lat: number | null;
      lng: number | null;
    }>
  ) {
    return apiRequest<{ device: Device }>(`/devices/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
  }

  delete(id: string) {
    return apiRequest<void>(`/devices/${id}`, { method: 'DELETE' });
  }

  setFavorite(id: string, favorite: boolean) {
    return apiRequest<{ device: Device }>(`/devices/${id}/favorite`, {
      method: 'PUT',
      body: JSON.stringify({ favorite }),
    });
  }

  setConnectivityNotifications(id: string, enabled: boolean) {
    return apiRequest<{ device: Device }>(`/devices/${id}/connectivity-notifications`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    });
  }

  addComponent(id: string, input: { kind: ComponentKind; name?: string | null }) {
    return apiRequest<{ component: DeviceComponent }>(`/devices/${id}/components`, { method: 'POST', body: JSON.stringify(input) });
  }

  updateComponent(id: string, componentId: string, input: { name?: string | null; enabled?: boolean }) {
    return apiRequest<{ component: DeviceComponent }>(`/devices/${id}/components/${componentId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  }

  removeComponent(id: string, componentId: string) {
    return apiRequest<void>(`/devices/${id}/components/${componentId}`, { method: 'DELETE' });
  }

  listValves(id: string) {
    return apiRequest<{ valves: ValveSummary[] }>(`/devices/${id}/valves`);
  }

  getThresholds(id: string) {
    return apiRequest<ThresholdsResponse>(`/devices/${id}/thresholds`);
  }

  updateThresholds(id: string, thresholds: StationThresholds) {
    return apiRequest<ThresholdsResponse>(`/devices/${id}/thresholds`, { method: 'PUT', body: JSON.stringify(thresholds) });
  }

  resetThresholds(id: string) {
    return apiRequest<ThresholdsResponse>(`/devices/${id}/thresholds`, { method: 'DELETE' });
  }
}
